import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { WalletContext } from '../../context/WalletContext';
import WalletPickerModal from '../WalletPickerModal';
import {
  HL_WS_URL, DEFAULT_MARKETS, LEVERAGES, HL_MIN_SIZE_USD,
} from './hyperliquidConstants';
import {
  fetchMeta, fetchAccountState,
  updateLeverage, placeMarketOrder, closePosition,
  depositUsdcToHL, ARB_CHAIN_ID,
  fmtPrice, getOrCreateAgent,
} from './hyperliquidUtils';
import axios from 'axios';
import { getInjectedProvider } from '../../utils/injectedProvider';

const API_BASE = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  ? 'http://localhost:3003'
  : 'https://api.blockhivetechnologies.com';

const ANALYTICS_TTL = 2 * 60 * 1000;
const HL_APP = 'https://app.hyperliquid.xyz';

function modeColor(mode, isHold) {
  if (isHold) return '#f7c948';
  if (mode === 'buy')  return '#26c49a';
  if (mode === 'sell') return '#c0392b';
  return '#555';
}
function modeFrame(mode, isHold) {
  if (isHold)          return { bg: 'rgba(247,201,72,0.10)',  border: 'rgba(247,201,72,0.45)' };
  if (mode === 'buy')  return { bg: 'rgba(38,196,154,0.10)',  border: 'rgba(38,196,154,0.45)' };
  if (mode === 'sell') return { bg: 'rgba(192,57,43,0.10)',   border: 'rgba(192,57,43,0.45)'  };
  return               { bg: 'rgba(85,85,85,0.08)',           border: 'rgba(85,85,85,0.25)'   };
}

const HL_CHAIN_ID = 999; // HyperEVM mainnet

async function switchToHyperEVM(rawProvider) {
  const p = rawProvider || getInjectedProvider();
  const chainIdHex = '0x3E7'; // 999
  try {
    await p.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: chainIdHex }] });
  } catch (e) {
    if (e.code === 4902) {
      try {
        await p.request({
          method: 'wallet_addEthereumChain',
          params: [{
            chainId: chainIdHex,
            chainName: 'HyperEVM',
            nativeCurrency: { name: 'HYPE', symbol: 'HYPE', decimals: 18 },
            rpcUrls: ['https://rpc.hyperliquid.xyz/evm'],
            blockExplorerUrls: ['https://purrsec.com'],
          }],
        });
      } catch (addErr) {
        console.warn('[HyperliquidPanel] add network:', addErr.message);
      }
    } else {
      console.warn('[HyperliquidPanel] network switch:', e.message);
    }
  }
}

export default function HyperliquidPanel({ market, setMarket }) {
  const { connected, address, signer, chainId, disconnectWallet } = useContext(WalletContext);

  // ── market metadata ───────────────────────────────────────────────────────
  const [assets,    setAssets]    = useState([]); // full list from meta
  const [contexts,  setContexts]  = useState([]); // mark prices, funding per asset
  const [metaReady, setMetaReady] = useState(false);

  // ── real-time prices (allMids WS) ─────────────────────────────────────────
  const [mids, setMids] = useState({}); // { BTC: "95123.5", ... }

  // ── account state ─────────────────────────────────────────────────────────
  const [accountValue,   setAccountValue]   = useState(null);
  const [spotBalances,   setSpotBalances]   = useState([]);
  const [positions,      setPositions]      = useState([]);
  const [acctLoading,    setAcctLoading]    = useState(false);
  const [acctFetched,    setAcctFetched]    = useState(false);
  const [notRegistered,  setNotRegistered]  = useState(false);

  // ── order state ───────────────────────────────────────────────────────────
  const [side,         setSide]         = useState('long');
  const [leverage,     setLeverage]     = useState(10);
  const [collateral,   setCollateral]   = useState('');
  const [status,       setStatus]       = useState('');
  const [loading,      setLoading]      = useState(false);
  const [pendingOrder, setPendingOrder] = useState(null);
  const [pendingClose, setPendingClose] = useState(null);
  const [closeStatus,  setCloseStatus]  = useState('');
  const [showPicker,   setShowPicker]   = useState(false);

  // ── deposit state ─────────────────────────────────────────────────────────
  const [showDeposit,    setShowDeposit]    = useState(false);
  const [depositAmt,     setDepositAmt]     = useState('');
  const [depositStatus,  setDepositStatus]  = useState('');
  const [depositLoading, setDepositLoading] = useState(false);

  // ── analytics ─────────────────────────────────────────────────────────────
  const [analytics, setAnalytics] = useState(null);
  const analyticsTsRef = useRef(0);

  // ── websocket refs ────────────────────────────────────────────────────────
  const wsRef      = useRef(null);
  const wsReconnRef = useRef(null);
  const wsMounted  = useRef(true);

  // ─────────────────────────────────────────────────────────────────────────
  // Derived values
  // ─────────────────────────────────────────────────────────────────────────
  const asset     = assets.find(a => a.name === market) || assets[0];
  const ctx       = asset ? contexts[asset.index] : null;
  const markPx    = mids[market] || ctx?.markPx || '0';
  const markPrice = parseFloat(markPx) || 0;
  const collAmt   = parseFloat(collateral) || 0;
  const sizeUsd   = collAmt * leverage;
  const liqEst    = markPrice > 0 && collAmt > 0
    ? (side === 'long'
        ? markPrice * (1 - 0.9 / leverage)
        : markPrice * (1 + 0.9 / leverage))
    : 0;
  const maxLev    = asset ? Math.min(asset.maxLeverage, 50) : 50;
  const funding   = ctx?.funding ? (parseFloat(ctx.funding) * 100).toFixed(4) : null;

  // Spot USDC is usable for perps in HL Unified mode — combine with perps balance
  const spotUsdcBalance  = spotBalances.find(b => b.coin === 'USDC')?.total || 0;
  const effectiveBalance = (accountValue || 0) + spotUsdcBalance;
  // Non-USDC spot tokens (e.g. HYPE) shown separately
  const otherSpot        = spotBalances.filter(b => b.coin !== 'USDC');

  // ─────────────────────────────────────────────────────────────────────────
  // Meta fetch (on mount)
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    fetchMeta()
      .then(({ assets: a, contexts: c }) => {
        setAssets(a);
        setContexts(c);
        setMetaReady(true);
      })
      .catch(e => console.error('[HyperliquidPanel] meta:', e));
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // WebSocket — allMids real-time prices
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    wsMounted.current = true;

    function connect() {
      if (!wsMounted.current) return;
      const ws = new WebSocket(HL_WS_URL);
      wsRef.current = ws;

      ws.onopen = () => {
        ws.send(JSON.stringify({ method: 'subscribe', subscription: { type: 'allMids' } }));
      };

      ws.onmessage = e => {
        try {
          const msg = JSON.parse(e.data);
          if (msg.channel === 'allMids' && msg.data?.mids) {
            setMids(prev => ({ ...prev, ...msg.data.mids }));
          }
        } catch {}
      };

      ws.onclose = () => {
        wsRef.current = null;
        if (wsMounted.current) wsReconnRef.current = setTimeout(connect, 3000);
      };
      ws.onerror = () => ws.close();
    }

    connect();
    return () => {
      wsMounted.current = false;
      clearTimeout(wsReconnRef.current);
      if (wsRef.current) { wsRef.current.onclose = null; wsRef.current.close(); }
    };
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // Account state (poll every 15s when connected)
  // ─────────────────────────────────────────────────────────────────────────
  const refreshAccount = useCallback(async () => {
    if (!connected || !address) return;
    setAcctLoading(true);
    try {
      const state = await fetchAccountState(address);
      setAccountValue(state.accountValue);
      setSpotBalances(state.spotBalances || []);
      setPositions(state.positions);
      setNotRegistered(!!state.notRegistered);
      setAcctFetched(true);
    } catch (e) {
      setAcctFetched(true);
    } finally {
      setAcctLoading(false);
    }
  }, [connected, address]);

  useEffect(() => {
    refreshAccount();
    const t = setInterval(refreshAccount, 15_000);
    return () => clearInterval(t);
  }, [refreshAccount]);

  // Sync positions to localStorage so tpo_vp.html (same-origin iframe) can draw them
  useEffect(() => {
    if (!connected || !address) { localStorage.removeItem('hl_positions'); return; }
    const payload = positions.map(pos => ({
      side:   pos.isLong ? 'LONG' : 'SHORT',
      market: pos.coin + '/USD',
      entry:  pos.entryPx,
      liq:    pos.liquidationPx,
    }));
    localStorage.setItem('hl_positions', JSON.stringify(payload));
  }, [positions, connected, address]);

  // ─────────────────────────────────────────────────────────────────────────
  // Analytics (R values from our backend)
  // ─────────────────────────────────────────────────────────────────────────
  const fetchAnalytics = useCallback(async () => {
    if (Date.now() - analyticsTsRef.current < ANALYTICS_TTL && analytics) return;
    try {
      const res = await axios.get(`${API_BASE}/btc_today`, {
        timeout: 10000, headers: { 'ngrok-skip-browser-warning': 'true' },
      });
      setAnalytics({ rc: res.data.r_current, vp: res.data.volume_profile });
      analyticsTsRef.current = Date.now();
    } catch {}
  }, [analytics]);

  useEffect(() => {
    fetchAnalytics();
    const t = setInterval(fetchAnalytics, ANALYTICS_TTL);
    return () => clearInterval(t);
  }, [fetchAnalytics]);

  // ─────────────────────────────────────────────────────────────────────────
  // Confirm order → show modal
  // ─────────────────────────────────────────────────────────────────────────
  const execute = () => {
    if (!signer || !address)           { setStatus('Connect wallet first'); return; }
    if (notRegistered)                 { setStatus('No HL account — deposit USDC at app.hyperliquid.xyz first'); return; }
    if (collAmt <= 0)                  { setStatus('Enter collateral amount'); return; }
    if (markPrice <= 0)                { setStatus('Price not available — wait'); return; }
    if (sizeUsd < HL_MIN_SIZE_USD)     { setStatus(`Min position $${HL_MIN_SIZE_USD} — increase collateral or leverage`); return; }
    setPendingOrder({ collAmt, sizeUsd, leverage, side, markPrice, liqEst });
    setStatus('');
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Execute confirmed order
  // ─────────────────────────────────────────────────────────────────────────
  const executeConfirmed = async () => {
    const po = pendingOrder;
    setPendingOrder(null);
    setLoading(true);
    setStatus('Approving agent…');

    try {
      // Get or create ephemeral agent wallet — triggers ONE Rabby popup on first trade only
      const agentWallet = await getOrCreateAgent(signer, address);

      setStatus('Setting leverage…');
      await updateLeverage(agentWallet, asset.index, po.leverage);

      setStatus('Placing order…');

      const resp = await placeMarketOrder(agentWallet, {
        assetIndex: asset.index,
        coin:       asset.name,
        isBuy:      po.side === 'long',
        sizeUsd:    po.sizeUsd,
        markPx:     po.markPrice.toString(),
        szDecimals: asset.szDecimals,
      });

      const filled  = resp?.data?.statuses?.[0]?.filled;
      const resting = resp?.data?.statuses?.[0]?.resting;

      if (filled) {
        setStatus(`Filled — avg $${parseFloat(filled.avgPx).toLocaleString()} · size ${filled.totalSz}`);
      } else if (resting) {
        setStatus(`Order resting (oid ${resting.oid}) — may not fill at market`);
      } else {
        setStatus('Order submitted');
      }

      setCollateral('');
      refreshAccount();
    } catch (err) {
      setStatus(err?.message?.slice(0, 100) || 'Order failed');
    } finally {
      setLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Close position
  // ─────────────────────────────────────────────────────────────────────────
  const executeClose = async () => {
    const pos = pendingClose;
    setPendingClose(null);
    setCloseStatus('Closing…');

    const closingAsset = assets.find(a => a.name === pos.coin);
    if (!closingAsset) { setCloseStatus('Asset not found'); return; }

    const closeMarkPx = mids[pos.coin] || contexts[closingAsset.index]?.markPx || '0';
    if (parseFloat(closeMarkPx) <= 0) { setCloseStatus('Price unavailable — retry'); return; }

    try {
      const agentWallet = await getOrCreateAgent(signer, address);
      const resp = await closePosition(agentWallet, {
        assetIndex: closingAsset.index,
        coin:       pos.coin,
        isLong:     pos.isLong,
        sizeAbs:    pos.sizeAbs,
        markPx:     closeMarkPx,
        szDecimals: closingAsset.szDecimals,
      });

      const filled = resp?.data?.statuses?.[0]?.filled;
      setCloseStatus(filled
        ? `Closed — avg $${parseFloat(filled.avgPx).toLocaleString()}`
        : 'Close submitted');
      refreshAccount();
    } catch (err) {
      setCloseStatus(err?.message?.slice(0, 100) || 'Close failed');
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Deposit handler
  // ─────────────────────────────────────────────────────────────────────────
  const executeDeposit = async () => {
    const amt = parseFloat(depositAmt);
    if (!amt || amt <= 0) { setDepositStatus('Enter amount'); return; }
    setDepositLoading(true);
    setDepositStatus('');

    const rawProvider = signer?.provider?.provider || getInjectedProvider();
    try {
      // Switch to Arbitrum for the deposit
      await rawProvider.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: '0xA4B1' }],
      });
      setDepositStatus('Check wallet — sign authorization…');
      const tx = await depositUsdcToHL(signer, address, amt);
      setDepositStatus(`Submitted — tx ${tx.hash.slice(0, 10)}… (5-10 min to credit)`);
      setDepositAmt('');
      // Switch back to HyperEVM after deposit
      await rawProvider.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: '0x3E7' }],
      }).catch(() => {});
    } catch (err) {
      setDepositStatus(err?.message?.slice(0, 120) || 'Deposit failed');
    } finally {
      setDepositLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Market tabs — show DEFAULT_MARKETS first, then rest
  // ─────────────────────────────────────────────────────────────────────────
  const visibleMarkets = metaReady
    ? [...DEFAULT_MARKETS.filter(n => assets.find(a => a.name === n)),
       ...assets.filter(a => !DEFAULT_MARKETS.includes(a.name)).map(a => a.name).slice(0, 4)]
    : DEFAULT_MARKETS;

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="tc-gmx-panel">

      {/* Wallet bar */}
      {connected ? (
        <>
          <div className="tc-wallet-bar">
            <span className="tc-wallet-addr">{address?.slice(0, 6)}…{address?.slice(-4)}</span>
            <button
              onClick={() => { setShowDeposit(v => !v); setDepositStatus(''); }}
              style={{
                marginLeft: 'auto', background: 'none', border: '1px solid rgba(0,238,255,0.25)',
                color: '#0ef8f8', fontSize: 8, letterSpacing: '1px', padding: '2px 7px',
                borderRadius: 3, cursor: 'pointer', fontFamily: 'inherit', marginRight: 6,
              }}
            >DEPOSIT</button>
            <button
              onClick={disconnectWallet}
              style={{
                background: 'none', border: '1px solid rgba(255,255,255,0.1)',
                color: '#555', fontSize: 8, letterSpacing: '1px', padding: '2px 7px',
                borderRadius: 3, cursor: 'pointer', fontFamily: 'inherit', marginRight: 6,
              }}
            >DISCONNECT</button>
            <a
              href={`${HL_APP}/portfolio`}
              target="_blank" rel="noreferrer"
              style={{ fontSize: 8, color: '#555', letterSpacing: '1px', textDecoration: 'none' }}
            >HL ↗</a>
          </div>

          {/* Deposit panel */}
          {showDeposit && (
            <div style={{
              margin: '4px 0 8px', padding: '10px 12px', borderRadius: 4,
              background: 'rgba(0,238,255,0.04)', border: '1px solid rgba(0,238,255,0.15)',
            }}>
              <div style={{ fontSize: 8, color: '#0ef8f8', letterSpacing: '1.5px', marginBottom: 8 }}>
                DEPOSIT USDC → HYPERLIQUID
              </div>
              <div style={{ fontSize: 8, color: '#555', letterSpacing: '0.5px', marginBottom: 8 }}>
                Switches to Arbitrum, signs authorization, switches back to HyperEVM.
              </div>
              <div className="tc-input-group" style={{ marginBottom: 8 }}>
                <input
                  type="number" className="tc-input" placeholder="0.00"
                  value={depositAmt} onChange={e => setDepositAmt(e.target.value)} min="0"
                />
                <span className="tc-input-suffix">USDC</span>
              </div>
              <button
                className="tc-execute-btn long"
                onClick={executeDeposit}
                disabled={depositLoading || !depositAmt}
                style={{ marginBottom: depositStatus ? 6 : 0 }}
              >
                {depositLoading ? 'DEPOSITING…' : 'DEPOSIT'}
              </button>
              {depositStatus && (
                <div className="tc-status" style={{ marginTop: 4 }}>{depositStatus}</div>
              )}
            </div>
          )}
        </>
      ) : (
        <div className="tc-wallet-bar tc-wallet-bar--disconnected">
          <button className="tc-wallet-connect-btn" onClick={() => setShowPicker(true)}>
            CONNECT WALLET
          </button>
          <WalletPickerModal
            open={showPicker}
            onClose={() => setShowPicker(false)}
            onConnected={() => setShowPicker(false)}
          />
        </div>
      )}

      {/* Wrong network banner */}
      {connected && chainId && chainId !== HL_CHAIN_ID && (
        <div style={{
          margin: '6px 0', padding: '8px 12px', borderRadius: 4,
          background: 'rgba(247,201,72,0.08)', border: '1px solid rgba(247,201,72,0.35)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
        }}>
          <span style={{ fontSize: 8, color: '#f7c948', letterSpacing: '0.8px' }}>
            WRONG NETWORK — switch to HyperEVM
          </span>
          <button
            onClick={() => switchToHyperEVM(signer?.provider?.provider)}
            style={{
              background: 'rgba(247,201,72,0.15)', border: '1px solid rgba(247,201,72,0.4)',
              color: '#f7c948', fontSize: 8, letterSpacing: '1px', padding: '3px 8px',
              borderRadius: 3, cursor: 'pointer', fontFamily: 'inherit',
            }}
          >SWITCH</button>
        </div>
      )}

      {/* Market — BTC only */}
      <div className="tc-row">
        <span className="tc-label">MARKET</span>
        <span style={{ fontSize: 11, color: '#0ef8f8', letterSpacing: '1.5px', fontWeight: 700 }}>BTC</span>
      </div>

      {/* Price + funding */}
      <div className="tc-row tc-price-row">
        <span className="tc-label">PRICE</span>
        <span className="tc-price-value" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
          <span>{markPrice > 0 ? `$${fmtPrice(markPx, market)}` : '—'}</span>
          {funding && (
            <span style={{ fontSize: 8, color: parseFloat(funding) >= 0 ? '#26c49a' : '#c0392b', letterSpacing: '0.5px' }}>
              {parseFloat(funding) >= 0 ? '+' : ''}{funding}% / 8h
            </span>
          )}
        </span>
      </div>

      {/* Analytics (BTC only) */}
      {market === 'BTC' && analytics?.rc && (<>
        <div className="tc-row tc-summary-row">
          <span className="tc-label">R 4H</span>
          {(() => { const f = modeFrame(analytics.rc.mode_4h, analytics.rc.hold_4h); return (
            <span className="tc-summary-val" style={{
              color: modeColor(analytics.rc.mode_4h, analytics.rc.hold_4h),
              background: f.bg, border: `1px solid ${f.border}`,
              borderRadius: 2, padding: '2px 6px', fontSize: 9,
            }}>{analytics.rc.r_4h >= 0 ? '+' : ''}{analytics.rc.r_4h?.toFixed(2) ?? '—'}</span>
          ); })()}
        </div>
        <div className="tc-row tc-summary-row" style={{ marginBottom: 14 }}>
          <span className="tc-label">R DAILY</span>
          {(() => { const f = modeFrame(analytics.rc.mode_daily, analytics.rc.hold_daily); return (
            <span className="tc-summary-val" style={{
              color: modeColor(analytics.rc.mode_daily, analytics.rc.hold_daily),
              background: f.bg, border: `1px solid ${f.border}`,
              borderRadius: 2, padding: '2px 6px', fontSize: 9,
            }}>{analytics.rc.r_daily >= 0 ? '+' : ''}{analytics.rc.r_daily?.toFixed(2) ?? '—'}</span>
          ); })()}
        </div>
      </>)}
      {market === 'BTC' && analytics?.vp && (
        <div className="tc-row tc-summary-row" style={{ marginBottom: 14 }}>
          <span className="tc-label">VOL PROFILE</span>
          <span style={{ display: 'flex', gap: 10 }}>
            {[
              { label: 'VAL', value: analytics.vp.val, color: '#888' },
              { label: 'POC', value: analytics.vp.poc, color: '#0ef8f8' },
              { label: 'VAH', value: analytics.vp.vah, color: '#888' },
            ].map(({ label, value, color }) => (
              <span key={label} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                <span style={{ color, fontSize: 8, letterSpacing: '0.8px' }}>{label}</span>
                <span style={{ color, fontSize: 10 }}>{value?.toLocaleString()}</span>
              </span>
            ))}
          </span>
        </div>
      )}

      {/* Long / Short */}
      <div className="tc-row">
        <button className={`tc-side-btn long  ${side === 'long'  ? 'active' : ''}`} onClick={() => setSide('long')}>LONG</button>
        <button className={`tc-side-btn short ${side === 'short' ? 'active' : ''}`} onClick={() => setSide('short')}>SHORT</button>
      </div>

      {/* Collateral input */}
      <div className="tc-row tc-input-row">
        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
          <span className="tc-label">COLLATERAL</span>
          {accountValue !== null && (
            <span style={{ fontSize: 8, color: '#444', letterSpacing: '0.5px' }}>
              HL <span style={{ color: '#666' }}>${effectiveBalance.toFixed(2)}</span>
            </span>
          )}
        </div>
        <div className="tc-input-group">
          <input
            type="number" className="tc-input" placeholder="0.00"
            value={collateral} onChange={e => setCollateral(e.target.value)} min="0"
          />
          <span className="tc-input-suffix">USDC</span>
        </div>
        {effectiveBalance > 0 && (
          <div style={{ width: '100%', marginTop: 6 }}>
            <input
              type="range" min={0} max={100} step={1}
              value={effectiveBalance > 0 ? Math.round((collAmt / effectiveBalance) * 100) : 0}
              onChange={e => {
                const pct = parseFloat(e.target.value);
                const amt = Math.floor(effectiveBalance * pct) / 100;
                setCollateral(amt > 0 ? amt.toFixed(2) : '');
              }}
              style={{ width: '100%', accentColor: '#26c49a', cursor: 'pointer' }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 2 }}>
              {[0, 25, 50, 75, 100].map(pct => (
                <button
                  key={pct}
                  onClick={() => { const amt = Math.floor(effectiveBalance * pct) / 100; setCollateral(amt > 0 ? amt.toFixed(2) : ''); }}
                  style={{
                    background: 'none', border: 'none', padding: '1px 0',
                    fontSize: 8, cursor: 'pointer', fontFamily: 'inherit', letterSpacing: '0.3px',
                    color: Math.round((collAmt / effectiveBalance) * 100) === pct ? '#26c49a' : '#444',
                  }}
                >{pct}%</button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Non-USDC HL spot tokens (USDC spot is already included in effectiveBalance above) */}
      {connected && otherSpot.length > 0 && (
        <div className="tc-row tc-summary-row">
          <span className="tc-label">HL SPOT</span>
          <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3 }}>
            {otherSpot.map(b => {
              const hypeAsset = assets.find(a => a.name === 'HYPE');
              const hypeCtxPx = hypeAsset ? parseFloat(contexts[hypeAsset.index]?.markPx || 0) : 0;
              const hypePrice = parseFloat(mids['HYPE'] || 0) || hypeCtxPx;
              const usdVal    = b.coin === 'HYPE' && hypePrice > 0
                ? `$${(b.total * hypePrice).toFixed(2)}` : null;
              return (
                <span key={b.coin} style={{ fontSize: 9, letterSpacing: '0.5px' }}>
                  <span style={{ color: '#666' }}>{b.total.toFixed(4)} {b.coin}</span>
                  {usdVal && <span style={{ color: '#0ef8f8', marginLeft: 5 }}>{usdVal}</span>}
                </span>
              );
            })}
          </span>
        </div>
      )}

      {/* Leverage */}
      <div className="tc-row">
        <span className="tc-label">LEVERAGE</span>
        <div className="tc-lev-tabs">
          {LEVERAGES.filter(l => l <= maxLev).map(l => (
            <button
              key={l}
              className={`tc-lev-tab ${leverage === l ? 'active' : ''}`}
              onClick={() => setLeverage(l)}
            >{l}×</button>
          ))}
        </div>
      </div>

      {/* Size summary */}
      <div className="tc-row tc-summary-row">
        <span className="tc-label">SIZE</span>
        <span className="tc-summary-val">
          ${sizeUsd > 0 ? sizeUsd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
        </span>
      </div>

      {/* Liquidation estimate */}
      {liqEst > 0 && (
        <div className="tc-row tc-summary-row">
          <span className="tc-label">LIQ. EST. <span style={{ fontSize: 7, color: '#444' }}>(approx)</span></span>
          <span className="tc-summary-val tc-liq">~${fmtPrice(liqEst.toString(), market)}</span>
        </div>
      )}

      {/* Exec fee note — HL has none */}
      <div className="tc-row tc-fee-note">
        <span className="tc-label">EXEC FEE</span>
        <span className="tc-fee-val">none — immediate CLOB fill</span>
      </div>

      {/* Execute button */}
      {!connected ? (
        <button className="tc-execute-btn long" disabled style={{ opacity: 0.3 }}>CONNECT WALLET ABOVE</button>
      ) : (
        <>
          <button
            className={`tc-execute-btn ${side}`}
            onClick={execute}
            disabled={loading || collAmt <= 0 || (connected && !acctFetched)}
          >
            {loading ? 'SENDING…' : `${side === 'long' ? 'LONG' : 'SHORT'} ${market}`}
          </button>
          {status && <div className="tc-status">{status}</div>}
        </>
      )}

      {/* Not registered — address has never deposited to HL */}
      {connected && notRegistered && (
        <div style={{
          marginTop: 10, padding: '10px 12px', borderRadius: 4,
          background: 'rgba(255,160,0,0.08)', border: '1px solid rgba(255,160,0,0.3)',
          fontSize: 9, letterSpacing: '0.8px', lineHeight: 1.6, color: '#ccc',
        }}>
          <div style={{ color: '#f7c948', marginBottom: 4, letterSpacing: '1.5px' }}>WALLET NOT ON HYPERLIQUID</div>
          <div style={{ fontFamily: 'monospace', fontSize: 8, color: '#888', marginBottom: 4, wordBreak: 'break-all' }}>{address}</div>
          This address has no HL account. Make sure you're connected with the same wallet you used to deposit on app.hyperliquid.xyz.
          <a
            href={`${HL_APP}/portfolio`}
            target="_blank" rel="noreferrer"
            style={{ display: 'block', marginTop: 6, color: '#0ef8f8', textDecoration: 'none', letterSpacing: '1.5px' }}
          >
            OPEN HYPERLIQUID ↗
          </a>
        </div>
      )}


      {/* Open positions */}
      {connected && (
        <div className="tc-positions">
          <div className="tc-positions-header">
            <span className="tc-label">POSITIONS</span>
            <span className="tc-positions-refresh" onClick={refreshAccount} title="Refresh">↻</span>
          </div>
          {closeStatus && <div className="tc-status" style={{ margin: '0 0 4px' }}>{closeStatus}</div>}
          {acctLoading && positions.length === 0 ? (
            <div className="tc-positions-empty">loading…</div>
          ) : positions.length === 0 ? (
            <div className="tc-positions-empty">no open positions</div>
          ) : positions.map(pos => {
            const posMarkPx = parseFloat(mids[pos.coin] || contexts[assets.find(a => a.name === pos.coin)?.index || 0]?.markPx || 0);
            const pnl    = pos.unrealizedPnl;
            const pnlPct = accountValue > 0 ? pnl / accountValue * 100 : 0;
            return (
              <div key={pos.coin} className="tc-position-card">
                <div className="tc-position-top">
                  <span className={`tc-position-side ${pos.isLong ? 'long' : 'short'}`}>
                    {pos.coin} · {pos.isLong ? 'LONG' : 'SHORT'} {pos.leverage?.value}×
                  </span>
                  <button className="tc-close-btn" onClick={() => setPendingClose(pos)}>CLOSE</button>
                </div>
                <div className="tc-position-stats">
                  <span>
                    <span className="tc-label">SIZE </span>
                    <span className="tc-position-val">${pos.positionValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                  </span>
                  <span>
                    <span className="tc-label">ENTRY </span>
                    <span className="tc-position-val">${fmtPrice(pos.entryPx.toString(), pos.coin)}</span>
                  </span>
                  <span>
                    <span className="tc-label">MARK </span>
                    <span className="tc-position-val">{posMarkPx > 0 ? `$${fmtPrice(posMarkPx.toString(), pos.coin)}` : '—'}</span>
                  </span>
                  <span>
                    <span className="tc-label">PNL </span>
                    <span className={`tc-position-pnl ${pnl >= 0 ? 'pos' : 'neg'}`}>
                      {pnl >= 0 ? '+' : ''}{pnl.toFixed(2)} ({pnlPct >= 0 ? '+' : ''}{pnlPct.toFixed(1)}%)
                    </span>
                  </span>
                  {pos.liquidationPx && (
                    <span>
                      <span className="tc-label">LIQ </span>
                      <span className="tc-position-val tc-liq">~${fmtPrice(pos.liquidationPx.toString(), pos.coin)}</span>
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Order confirmation modal */}
      {pendingOrder && (
        <div className="tc-modal-overlay" onClick={() => setPendingOrder(null)}>
          <div className="tc-modal" onClick={e => e.stopPropagation()}>
            <div className="tc-modal-title">CONFIRM ORDER</div>
            <div className="tc-modal-row"><span>VENUE</span><span style={{ color: '#0ef8f8' }}>HYPERLIQUID</span></div>
            <div className="tc-modal-row"><span>MARKET</span><span>{market}</span></div>
            <div className="tc-modal-row">
              <span>SIDE</span>
              <span style={{ color: pendingOrder.side === 'long' ? '#26c49a' : '#c0392b', fontWeight: 700 }}>
                {pendingOrder.side.toUpperCase()}
              </span>
            </div>
            <div className="tc-modal-row"><span>COLLATERAL</span><span>${pendingOrder.collAmt.toFixed(2)} USDC</span></div>
            <div className="tc-modal-row"><span>LEVERAGE</span><span>{pendingOrder.leverage}×</span></div>
            <div className="tc-modal-row"><span>SIZE</span><span>${pendingOrder.sizeUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span></div>
            <div className="tc-modal-row"><span>MARK</span><span>${fmtPrice(pendingOrder.markPrice.toString(), market)}</span></div>
            <div className="tc-modal-row">
              <span>LIQ. EST.</span>
              <span style={{ color: '#e67e22' }}>~${fmtPrice(pendingOrder.liqEst.toString(), market)}</span>
            </div>
            <div className="tc-modal-row" style={{ fontSize: 8, color: '#444' }}>
              <span>TYPE</span><span>MARKET IOC · 5% SLIPPAGE</span>
            </div>
            <div className="tc-modal-btns">
              <button className="tc-modal-cancel" onClick={() => setPendingOrder(null)}>CANCEL</button>
              <button className={`tc-modal-confirm ${pendingOrder.side}`} onClick={executeConfirmed}>CONFIRM</button>
            </div>
          </div>
        </div>
      )}

      {/* Close confirmation modal */}
      {pendingClose && (
        <div className="tc-modal-overlay" onClick={() => setPendingClose(null)}>
          <div className="tc-modal" onClick={e => e.stopPropagation()}>
            <div className="tc-modal-title">CONFIRM CLOSE</div>
            <div className="tc-modal-row"><span>MARKET</span><span>{pendingClose.coin}</span></div>
            <div className="tc-modal-row">
              <span>SIDE</span>
              <span style={{ color: pendingClose.isLong ? '#26c49a' : '#c0392b', fontWeight: 700 }}>
                {pendingClose.isLong ? 'LONG' : 'SHORT'}
              </span>
            </div>
            <div className="tc-modal-row"><span>SIZE</span><span>${pendingClose.positionValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span></div>
            <div className="tc-modal-row"><span>ENTRY</span><span>${fmtPrice(pendingClose.entryPx.toString(), pendingClose.coin)}</span></div>
            <div className="tc-modal-row">
              <span>CURRENT</span>
              <span>{parseFloat(mids[pendingClose.coin] || 0) > 0 ? `$${fmtPrice(mids[pendingClose.coin], pendingClose.coin)}` : '—'}</span>
            </div>
            <div className="tc-modal-row">
              <span>PNL</span>
              <span className={pendingClose.unrealizedPnl >= 0 ? 'tc-position-pnl pos' : 'tc-position-pnl neg'}>
                {pendingClose.unrealizedPnl >= 0 ? '+' : ''}{pendingClose.unrealizedPnl.toFixed(2)}
              </span>
            </div>
            <div className="tc-modal-btns">
              <button className="tc-modal-cancel" onClick={() => setPendingClose(null)}>CANCEL</button>
              <button
                className={`tc-modal-confirm ${pendingClose.isLong ? 'short' : 'long'}`}
                onClick={executeClose}
              >CLOSE POSITION</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
